import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SyncIcon from '@mui/icons-material/Sync';
import SaveIcon from '@mui/icons-material/Save';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import axios from 'axios';
import { getApiBaseUrl, getStoredToken } from '../../services/httpClient';
import {
  MITRA_CARD_ASPECT,
  MITRA_CARD_HEIGHT,
  MITRA_CARD_MAX_INPUT_BYTES,
  MITRA_CARD_TARGET_BYTES,
  MITRA_CARD_WIDTH,
  compressMitraCardImageForUpload,
  toOptimizedMitraCardDeliveryUrl,
} from '../../utils/compressImageUpload';
import {
  MITRA_ASSET_LIBRARY,
  MITRA_ASSET_LIBRARY_LABELS,
  mitraDefaultAssetUrl,
  resolveMitraCardPreviewUrl,
} from '../../utils/mitraDefaultAssets';
import MitraScreensBoard from './MitraScreensBoard';
import MitraFullScreenBoard from './MitraFullScreenBoard';
import {
  DEFAULT_ZONE_META,
  normalizeLayout,
  normalizeBlocks,
} from './mitraScreensBoardUtils';
import ShellChromeBoard from '../../components/ShellChromeBoard';

const api = axios.create({
  baseURL: `${getApiBaseUrl()}/mitra-screens`,
  timeout: 25000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['Content-Type'] = 'application/json';
  return config;
});

const chromeApi = axios.create({
  baseURL: `${getApiBaseUrl()}/app-shell-chrome`,
  timeout: 25000,
});

chromeApi.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['Content-Type'] = 'application/json';
  return config;
});

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dml5dugde';
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'locsy_business_profiles';

const BADGE_PRESETS = ['Manage', 'Live', 'Coupons', 'Info', 'Inbox', 'New', 'Hot', 'Promo', 'Season'];

const TITLE_SOFT_MAX = 28;
const SUBTITLE_SOFT_MAX = 42;
const BADGE_SOFT_MAX = 12;

const buildMitraCardPrompt = ({ title, subtitle, badge, aiBrief, zone }) => {
  const zoneLabel =
    zone === 'operate' ? 'My Business Desk' : 'Connect with Loksy';
  return `Create ONE landscape feature-card thumbnail for the LOCSY Mitra business app (${zoneLabel} hub).

Card title: ${String(title || '').trim() || '(unnamed)'}
Tagline: ${String(subtitle || '').trim() || 'N/A'}
Badge pill (optional, do NOT render as text in image): ${String(badge || '').trim() || 'none'}
Marketing brief: ${String(aiBrief || '').trim() || 'Clean modern Indian small-business tool tile'}

STRICT OUTPUT (dashboard card art only):
- Exact canvas: ${MITRA_CARD_WIDTH}×${MITRA_CARD_HEIGHT} pixels (3:2 landscape — NOT square, NOT 512×512 catalog product size)
- Style: premium 3D/illustrated product-marketing tile, soft depth, high contrast, readable when cropped to ~160px wide on phone
- Background: rich solid or subtle gradient that matches LOCSY Mitra (deep slate / teal / warm accent) — no busy photo collage
- Subject: single clear metaphor for the feature (catalog, ads, coupons, bills, leads, etc.) centered with breathing room
- NO readable text, logos, watermarks, UI chrome, phone frames, or badge stickers in the image (badge is overlaid by the app)
- File size target: under 200 KB (web). Prefer clean shapes over photo noise
- Output ONLY this one ${MITRA_CARD_WIDTH}×${MITRA_CARD_HEIGHT} image`;
};

const uploadMitraCardThumbnail = async (file) => {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Cloudinary is not configured.');
  }
  if (file.size > MITRA_CARD_MAX_INPUT_BYTES) {
    throw new Error('Image too large (max 12 MB).');
  }
  const prepared = await compressMitraCardImageForUpload(file);
  const formData = new FormData();
  formData.append('file', prepared);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'mitra_screen_cards');
  const cleanAxios = axios.create();
  if (cleanAxios.defaults.headers.common) {
    delete cleanAxios.defaults.headers.common.Authorization;
    delete cleanAxios.defaults.headers.common.authorization;
  }
  const response = await cleanAxios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    formData,
  );
  const secureUrl = response?.data?.secure_url || response?.data?.url || '';
  if (!secureUrl) throw new Error('Thumbnail upload failed');
  return toOptimizedMitraCardDeliveryUrl(secureUrl);
};

const emptyForm = () => ({
  title_en: '',
  title_te: '',
  title_hi: '',
  subtitle_en: '',
  subtitle_te: '',
  subtitle_hi: '',
  badge_en: '',
  badge_te: '',
  badge_hi: '',
  thumbnail_url: '',
  ai_brief: '',
  is_visible: true,
  display_order: 0,
});

/** Shows CDN or hardcoded Mitra webp; never leaves a blank dark box. */
const MitraCardThumb = ({ card, sx }) => {
  const src = resolveMitraCardPreviewUrl(card);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '100%', color: 'grey.400', px: 2, ...sx }}
      >
        <Typography variant="caption" textAlign="center">
          Missing image
          <br />
          {card?.default_asset_hint || card?.card_key || ''}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setFailed(true)}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        ...sx,
      }}
    />
  );
};

const MitraScreens = () => {
  const [pageTab, setPageTab] = useState('fullscreen'); // fullscreen | hub | bottom_nav | top_bar
  const [zone, setZone] = useState('operate');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [spec, setSpec] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [langTab, setLangTab] = useState('en');
  const [aiPrompt, setAiPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);
  const [showAssetLibrary, setShowAssetLibrary] = useState(true);
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [layout, setLayout] = useState(() => normalizeLayout(null));
  const [pageBlocks, setPageBlocks] = useState(() => normalizeBlocks(null));
  const [chrome, setChrome] = useState({ bottom_nav: [], top_bar: [] });
  const [chromeCatalog, setChromeCatalog] = useState({ bottom_nav: [], top_bar: [] });
  const [chromeDirty, setChromeDirty] = useState(false);
  const [chromeSaving, setChromeSaving] = useState(false);
  const thumbInputRef = useRef(null);
  const didAutoSync = useRef(false);

  const applyCardsPayload = (payload) => {
    setCards(payload?.data || []);
    setSpec(payload?.meta?.cardImageSpec || null);
    if (payload?.meta?.layout) {
      setLayout(normalizeLayout(payload.meta.layout));
    }
    if (payload?.meta?.blocks) {
      setPageBlocks(normalizeBlocks(payload.meta.blocks));
    }
    setOrderDirty(false);
  };

  const applyChromePayload = (data, meta) => {
    setChrome({
      bottom_nav: data?.bottom_nav || [],
      top_bar: data?.top_bar || [],
    });
    const cat = meta?.catalog;
    setChromeCatalog({
      bottom_nav: (cat?.bottom_nav || []).map((x) => x.item_key || x),
      top_bar: (cat?.top_bar || []).map((x) => x.item_key || x),
    });
    setChromeDirty(false);
  };

  const loadChrome = useCallback(async () => {
    try {
      const res = await chromeApi.get('/mitra');
      if (!res.data?.success) return;
      applyChromePayload(res.data.data, res.data.meta);
    } catch (e) {
      console.warn('Mitra shell chrome load failed', e);
    }
  }, []);

  const loadCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/all');
      if (!res.data?.success) {
        setError(res.data?.message || 'Failed to load Mitra screens');
        setCards([]);
        return;
      }
      applyCardsPayload(res.data);
      await loadChrome();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [loadChrome]);

  const saveChrome = async () => {
    setChromeSaving(true);
    try {
      const res = await chromeApi.put('/mitra', {
        bottom_nav: chrome.bottom_nav,
        top_bar: chrome.top_bar,
      });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Chrome save failed' });
        return;
      }
      applyChromePayload(res.data.data, res.data.meta);
      setSnack({
        severity: 'success',
        message:
          'Nav / top bar saved to this API. Mitra updates within ~20s or on app resume — API must match Mitra.',
      });
    } catch (e) {
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Chrome save failed',
      });
    } finally {
      setChromeSaving(false);
    }
  };

  /**
   * Two-way sync for ALL Mitra screen controls:
   * - Pull hub card catalog + shell chrome catalog into CMS (Mitra → React)
   * - Reload saved CMS (what Mitra reads on next refresh — React → Mitra)
   */
  const syncAllWithMitra = useCallback(
    async ({ quiet = false } = {}) => {
      setSyncing(true);
      if (!quiet) setError(null);
      try {
        const [cardsRes, chromeRes] = await Promise.all([
          api.post('/admin/sync-catalog'),
          chromeApi.post('/mitra/sync-catalog').catch((e) => ({
            data: {
              success: false,
              message: e.response?.data?.message || e.message,
            },
          })),
        ]);

        if (cardsRes.data?.success) {
          applyCardsPayload(cardsRes.data);
          setCatalogMeta(cardsRes.data.meta || null);
        }
        if (chromeRes.data?.success) {
          applyChromePayload(chromeRes.data.data, chromeRes.data.meta);
        } else {
          await loadChrome();
        }

        // Re-fetch so React matches what Mitra will read from this API.
        await loadCards();

        if (!quiet) {
          const cardN = cardsRes.data?.meta?.inserted?.length || 0;
          const blocksN = cardsRes.data?.meta?.blocksInserted?.length || 0;
          const chromeN = chromeRes.data?.meta?.inserted?.length || 0;
          const chromeOk = chromeRes.data?.success !== false;
          setSnack({
            severity: chromeOk ? 'success' : 'warning',
            message: chromeOk
              ? `Synced all controls ↔ Mitra (hub +${cardN}, blocks +${blocksN}, nav/top +${chromeN}). Mitra refreshes in ~20s / on resume.`
              : `Hub/blocks synced; nav/top bar sync failed: ${chromeRes.data?.message || 'check migration 156'}.`,
          });
        }
        return {
          success: true,
          data: cardsRes.data?.data || cards,
          cards: cardsRes.data,
          chrome: chromeRes.data,
        };
      } catch (e) {
        if (!quiet) {
          setSnack({
            severity: 'error',
            message: e.response?.data?.message || e.message || 'Sync failed',
          });
        }
        return null;
      } finally {
        setSyncing(false);
      }
    },
    [loadCards, loadChrome],
  );

  /** @deprecated alias — hub-only callers */
  const syncFromMitraApp = syncAllWithMitra;

  const apiBaseDisplay = getApiBaseUrl();
  const isLocalAdminApi =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Live load: sync missing app cards, then show CMS rows.
        if (!didAutoSync.current) {
          didAutoSync.current = true;
          const synced = await syncFromMitraApp({ quiet: true });
          if (cancelled) return;
          if (synced?.data) {
            setLoading(false);
            return;
          }
        }
        if (cancelled) return;
        await loadCards();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCards, syncFromMitraApp]);

  const zoneCards = useMemo(
    () => cards.filter((c) => c.zone === zone).sort((a, b) => a.display_order - b.display_order),
    [cards, zone],
  );

  const openEdit = (card) => {
    setEditing(card);
    setForm({
      title_en: card.title_en || '',
      title_te: card.title_te || '',
      title_hi: card.title_hi || '',
      subtitle_en: card.subtitle_en || '',
      subtitle_te: card.subtitle_te || '',
      subtitle_hi: card.subtitle_hi || '',
      badge_en: card.badge_en || '',
      badge_te: card.badge_te || '',
      badge_hi: card.badge_hi || '',
      thumbnail_url: card.thumbnail_url || '',
      ai_brief: card.ai_brief || '',
      is_visible: card.is_visible !== false,
      display_order: card.display_order ?? 0,
    });
    setLangTab('en');
    setAiPrompt('');
    setDialogOpen(true);
  };

  const handleGeneratePrompt = () => {
    if (!String(form.title_en || '').trim()) {
      setSnack({ severity: 'warning', message: 'Enter English title first (used in AI prompt).' });
      return;
    }
    setAiPrompt(
      buildMitraCardPrompt({
        title: form.title_en,
        subtitle: form.subtitle_en,
        badge: form.badge_en,
        aiBrief: form.ai_brief,
        zone: editing?.zone || zone,
      }),
    );
  };

  const handleCopyPrompt = async () => {
    if (!aiPrompt) {
      setSnack({ severity: 'warning', message: 'Generate a prompt first.' });
      return;
    }
    try {
      await navigator.clipboard.writeText(aiPrompt);
      setSnack({ severity: 'success', message: 'Prompt copied — paste in Gemini / ChatGPT.' });
    } catch {
      setSnack({ severity: 'error', message: 'Copy failed' });
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setThumbUploading(true);
    try {
      const url = await uploadMitraCardThumbnail(file);
      setForm((f) => ({ ...f, thumbnail_url: url }));
      setSnack({
        severity: 'success',
        message: `Uploaded & auto-cropped to ${MITRA_CARD_WIDTH}×${MITRA_CARD_HEIGHT} (${MITRA_CARD_ASPECT}).`,
      });
    } catch (e) {
      setSnack({ severity: 'error', message: e.message || 'Upload failed' });
    } finally {
      setThumbUploading(false);
      if (thumbInputRef.current) thumbInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!editing?.card_key) return;
    if (!String(form.title_en || '').trim()) {
      setSnack({ severity: 'warning', message: 'English title is required.' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/admin/${encodeURIComponent(editing.card_key)}`, form);
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Save failed' });
        return;
      }
      setSnack({ severity: 'success', message: 'Card updated — Mitra app picks this up on next refresh.' });
      setDialogOpen(false);
      await loadCards();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleResetThumb = async () => {
    if (!editing?.card_key) return;
    setSaving(true);
    try {
      const res = await api.post(`/admin/${encodeURIComponent(editing.card_key)}/reset-thumbnail`);
      if (res.data?.success) {
        setForm((f) => ({ ...f, thumbnail_url: '' }));
        setSnack({ severity: 'success', message: 'Reset to bundled Mitra asset.' });
        await loadCards();
      }
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = (cardKey) => {
    setCards((prev) =>
      prev.map((c) =>
        c.card_key === cardKey ? { ...c, is_visible: !c.is_visible } : c,
      ),
    );
    setOrderDirty(true);
  };

  const saveOrderVisibility = async () => {
    setBulkSaving(true);
    try {
      const payload = zoneCards.map((c) => ({
        card_key: c.card_key,
        is_visible: c.is_visible,
        display_order: c.display_order,
        section_key: c.section_key,
      }));
      const res = await api.put('/admin/bulk', {
        cards: payload,
        layout: {
          zones: layout.zones,
          sections: layout.sections,
        },
        blocks: {
          operate: pageBlocks.operate,
          grow: pageBlocks.grow,
        },
      });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Bulk save failed' });
        return;
      }
      if (res.data?.meta?.layout) {
        setLayout(normalizeLayout(res.data.meta.layout));
      }
      if (res.data?.meta?.blocks) {
        setPageBlocks(normalizeBlocks(res.data.meta.blocks));
      }
      setSnack({
        severity: 'success',
        message:
          'Full screen blocks, filters, rows & cards saved — Mitra picks up on resume / ~20s.',
      });
      setOrderDirty(false);
      await loadCards();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setBulkSaving(false);
    }
  };

  const zoneLabel = layout.zones?.[zone]?.label_en || DEFAULT_ZONE_META[zone]?.label_en || zone;
  const zoneBlurb = layout.zones?.[zone]?.blurb_en || DEFAULT_ZONE_META[zone]?.blurb_en || '';

  const previewTitle = form[`title_${langTab}`] || form.title_en || 'Card title';
  const previewSub = form[`subtitle_${langTab}`] || form.subtitle_en || 'Tagline';
  const previewBadge = form[`badge_${langTab}`] || form.badge_en || '';
  const previewImage =
    String(form.thumbnail_url || '').trim() ||
    mitraDefaultAssetUrl(editing?.default_asset_hint) ||
    '';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Mitra Screens
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Hub cards, bottom nav icons, and top-bar icons — hide, reorder (drag / arrows), or add
            optional screens. Routes stay in Flutter.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            color="primary"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={() => syncAllWithMitra()}
            disabled={loading || syncing || chromeSaving}
          >
            Sync all ↔ Mitra
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() =>
              pageTab === 'bottom_nav' || pageTab === 'top_bar'
                ? loadChrome()
                : loadCards()
            }
            disabled={loading || syncing || chromeSaving}
          >
            Refresh
          </Button>
          {pageTab === 'bottom_nav' || pageTab === 'top_bar' ? (
            <Button
              variant="contained"
              startIcon={chromeSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!chromeDirty || chromeSaving}
              onClick={saveChrome}
            >
              Save nav / top bar
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={bulkSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!orderDirty || bulkSaving}
              onClick={saveOrderVisibility}
            >
              {pageTab === 'fullscreen' ? 'Save full screen' : 'Save order / visibility'}
            </Button>
          )}
        </Stack>
      </Stack>

      {isLocalAdminApi ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>API mismatch risk:</strong> This admin panel talks to{' '}
          <code>{apiBaseDisplay}</code> (local). Mitra phone builds usually use{' '}
          <code>https://locsy-lq54y.ondigitalocean.app/api</code>. Saves here will{' '}
          <em>not</em> appear in the live Mitra app until you either (1) open admin on the
          DigitalOcean URL and Save again, or (2) point Mitra at your local API. Use{' '}
          <strong>Sync all ↔ Mitra</strong> after Save; then resume/reopen Mitra (~20s).
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Live sync:</strong> API <code>{apiBaseDisplay}</code>. After Save, Mitra pulls
          hub + nav/top bar within ~20s or on app resume. <strong>Sync all ↔ Mitra</strong> pulls
          missing catalog keys into this CMS and reloads what the app will read.
        </Alert>
      )}

      <Tabs
        value={pageTab}
        onChange={(_, v) => setPageTab(v)}
        sx={{ mb: 2, bgcolor: 'grey.100', borderRadius: 2, minHeight: 44 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="fullscreen" label="Full screen" />
        <Tab value="hub" label="Hub cards" />
        <Tab value="bottom_nav" label="Bottom nav" />
        <Tab value="top_bar" label="Top bar icons" />
      </Tabs>

      {pageTab === 'fullscreen' ? (
        <Box mb={2}>
          <Tabs
            value={zone}
            onChange={(_, v) => setZone(v)}
            sx={{ mb: 2, bgcolor: 'grey.100', borderRadius: 2, minHeight: 40 }}
          >
            <Tab value="operate" label={layout.zones?.operate?.label_en || 'My Business Desk'} />
            <Tab value="grow" label={layout.zones?.grow?.label_en || 'Connect with Loksy'} />
          </Tabs>
          {loading ? (
            <Box py={6} textAlign="center">
              <CircularProgress />
            </Box>
          ) : (
            <MitraFullScreenBoard
              zone={zone}
              blocks={pageBlocks[zone] || []}
              layout={layout}
              cards={cards}
              onBlocksChange={(next) => {
                setPageBlocks((prev) => ({ ...prev, [zone]: next }));
                setOrderDirty(true);
              }}
              onLayoutChange={(next) => {
                setLayout(normalizeLayout(next));
                setOrderDirty(true);
              }}
              onCardsChange={(next) => {
                setCards(next);
                setOrderDirty(true);
              }}
              onEditCard={openEdit}
              onToggleCardVisible={toggleVisible}
            />
          )}
        </Box>
      ) : null}

      {pageTab === 'bottom_nav' || pageTab === 'top_bar' ? (
        <Box mb={2}>
          {pageTab === 'bottom_nav' ? (
            <ShellChromeBoard
              title="Mitra bottom navigation"
              blurb="Home · Bills · Orders · Sales · My Ads · Favourites — drag to reorder, Off to hide, Add for optional screens (View Bills, Settings)."
              items={chrome.bottom_nav}
              addableKeys={chromeCatalog.bottom_nav}
              onChange={(next) => {
                setChrome((prev) => ({ ...prev, bottom_nav: next }));
                setChromeDirty(true);
              }}
            />
          ) : (
            <ShellChromeBoard
              title="Mitra top bar icons"
              blurb="Feedback · Language · Profile · Notifications — e.g. move Profile last or hide Feedback."
              items={chrome.top_bar}
              addableKeys={chromeCatalog.top_bar}
              onChange={(next) => {
                setChrome((prev) => ({ ...prev, top_bar: next }));
                setChromeDirty(true);
              }}
            />
          )}
        </Box>
      ) : null}

      {pageTab === 'hub' ? (
        <>
      <Alert severity="success" sx={{ mb: 2 }}>
        <strong>Live screens:</strong> Card keys match the Mitra Flutter hub. Opening this page
        syncs any new modules (My Ads, Shop Spin, Product / Service Groups, etc.). Edits here reach the app
        on the next Mitra refresh.
        {catalogMeta?.inserted?.length
          ? ` Last sync added: ${catalogMeta.inserted.join(', ')}.`
          : null}
      </Alert>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Card image spec:</strong> {MITRA_CARD_WIDTH}×{MITRA_CARD_HEIGHT}px ({MITRA_CARD_ASPECT}{' '}
        landscape) · target ~{Math.round(MITRA_CARD_TARGET_BYTES / 1024)} KB · auto-crop on upload.
        Catalog product thumbs stay <strong>512×512</strong> — different pipeline.
        Previews use <strong>current Mitra bundled images</strong> until you upload a custom
        replacement (or reset to app default).
        {spec?.contrastWithCatalog ? ` ${spec.contrastWithCatalog}.` : null}
      </Alert>

      <Alert severity="success" sx={{ mb: 2 }}>
        <strong>Locsy Spin (Shop):</strong> Mitra can offer a fair coupon wheel from Operate / Grow.
        Enable the <em>shop_screen</em> trigger and odds in{' '}
        <a href="/content/lucky-wheel">Locsy Spin</a>. Mitra still opts in per shop in-app. Bill
        auto-allot stays as-is.
      </Alert>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={800}>
          Current Mitra app images (by category)
        </Typography>
        <Button size="small" onClick={() => setShowAssetLibrary((v) => !v)}>
          {showAssetLibrary ? 'Hide library' : 'Show library'}
        </Button>
      </Stack>
      {showAssetLibrary ? (
        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            maxHeight: 320,
            overflow: 'auto',
          }}
        >
          {Object.entries(MITRA_ASSET_LIBRARY).map(([section, assets]) => (
            <Box key={section} mb={2}>
              <Typography variant="caption" fontWeight={800} color="text.secondary">
                {MITRA_ASSET_LIBRARY_LABELS[section] || section}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mt={0.75}>
                {assets.map((a) => {
                  const src = mitraDefaultAssetUrl(a.file);
                  return (
                    <Tooltip key={`${section}-${a.file}`} title={`${a.label} · ${a.file}`}>
                      <Box
                        sx={{
                          width: 96,
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: '#0F172A',
                        }}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt={a.label}
                          sx={{
                            width: '100%',
                            aspectRatio: section === 'quick_access' ? '1 / 1' : '3 / 2',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            px: 0.5,
                            py: 0.35,
                            bgcolor: 'white',
                            fontSize: 9,
                            lineHeight: 1.2,
                            height: 28,
                            overflow: 'hidden',
                          }}
                        >
                          {a.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Box>
      ) : null}

      <Tabs
        value={zone}
        onChange={(_, v) => setZone(v)}
        sx={{ mb: 2, bgcolor: 'grey.100', borderRadius: 2, minHeight: 44 }}
      >
        <Tab
          value="operate"
          label={layout.zones?.operate?.label_en || 'My Business Desk'}
        />
        <Tab
          value="grow"
          label={layout.zones?.grow?.label_en || 'Connect with Loksy'}
        />
      </Tabs>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'flex-start' }}
        mb={2}
      >
        <TextField
          size="small"
          label="Tab name (editable)"
          value={layout.zones?.[zone]?.label_en || ''}
          onChange={(e) => {
            setLayout((prev) => ({
              ...prev,
              zones: {
                ...prev.zones,
                [zone]: {
                  ...prev.zones[zone],
                  label_en: e.target.value.slice(0, 120),
                },
              },
            }));
            setOrderDirty(true);
          }}
          sx={{ minWidth: 260, bgcolor: 'background.paper' }}
        />
        <TextField
          size="small"
          fullWidth
          label="Tab blurb"
          value={layout.zones?.[zone]?.blurb_en || ''}
          onChange={(e) => {
            setLayout((prev) => ({
              ...prev,
              zones: {
                ...prev.zones,
                [zone]: {
                  ...prev.zones[zone],
                  blurb_en: e.target.value.slice(0, 255),
                },
              },
            }));
            setOrderDirty(true);
          }}
          sx={{ bgcolor: 'background.paper' }}
        />
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>{zoneLabel}:</strong> {zoneBlurb} Use <strong>row ↑↓</strong> (beside the
        heading) to move whole categories (e.g. Bills above Products). Use{' '}
        <strong>card ←→</strong> or drag cards within/across rows. Drag the ⋮⋮ handle on a row
        to reorder sections.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box py={8} textAlign="center">
          <CircularProgress />
        </Box>
      ) : (
        <MitraScreensBoard
          zone={zone}
          cards={cards}
          layout={layout}
          onCardsChange={(next) => {
            setCards(next);
            setOrderDirty(true);
          }}
          onLayoutChange={(next) => {
            setLayout(normalizeLayout(next));
            setOrderDirty(true);
          }}
          onEditCard={openEdit}
          onToggleVisible={toggleVisible}
        />
      )}
        </>
      ) : null}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit · {editing?.label_admin || editing?.card_key}
          <Typography variant="caption" display="block" color="text.secondary">
            Key locked: {editing?.card_key} (destination screen is not editable — prevents live breaks)
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Tabs value={langTab} onChange={(_, v) => setLangTab(v)} sx={{ mb: 1 }}>
                <Tab value="en" label="English" />
                <Tab value="te" label="తెలుగు" />
                <Tab value="hi" label="हिन्दी" />
              </Tabs>
              <TextField
                fullWidth
                required={langTab === 'en'}
                label={`Name (${langTab})`}
                value={form[`title_${langTab}`] || ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [`title_${langTab}`]: e.target.value.slice(0, 120) }))
                }
                margin="dense"
                helperText={`${(form[`title_${langTab}`] || '').length}/${TITLE_SOFT_MAX} soft max · mobile truncation`}
                error={langTab === 'en' && !(form.title_en || '').trim()}
              />
              <TextField
                fullWidth
                label={`Tagline (${langTab})`}
                value={form[`subtitle_${langTab}`] || ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [`subtitle_${langTab}`]: e.target.value.slice(0, 220) }))
                }
                margin="dense"
                helperText={`${(form[`subtitle_${langTab}`] || '').length}/${SUBTITLE_SOFT_MAX} soft max · benefit-led copy`}
              />
              <TextField
                fullWidth
                label={`Badge (${langTab})`}
                value={form[`badge_${langTab}`] || ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [`badge_${langTab}`]: e.target.value.slice(0, 40) }))
                }
                margin="dense"
                helperText={`${(form[`badge_${langTab}`] || '').length}/${BADGE_SOFT_MAX} · leave empty for no pill`}
              />
              <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.5} mb={1}>
                {BADGE_PRESETS.map((b) => (
                  <Chip
                    key={b}
                    size="small"
                    label={b}
                    onClick={() => setForm((f) => ({ ...f, [`badge_${langTab}`]: b }))}
                    variant={form[`badge_${langTab}`] === b ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Marketing brief (feeds AI prompt)"
                value={form.ai_brief}
                onChange={(e) => setForm((f) => ({ ...f, ai_brief: e.target.value }))}
                margin="dense"
                placeholder="e.g. Festive Diwali push for coupon manager — warm gold accents, gift/tag metaphor"
              />

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={form.is_visible}
                    onChange={(e) => setForm((f) => ({ ...f, is_visible: e.target.checked }))}
                  />
                }
                label="Visible in Mitra"
              />

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography fontWeight={700}>AI card art prompt</Typography>
                <Chip
                  size="small"
                  label={`${MITRA_CARD_WIDTH}×${MITRA_CARD_HEIGHT} · ${MITRA_CARD_ASPECT}`}
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Generate → copy → paste in Gemini / ChatGPT → download → upload here. Prompt forces
                landscape Mitra card size (not catalog 512×512).
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGeneratePrompt}
                >
                  Auto generate prompt
                </Button>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyPrompt}>
                  Copy
                </Button>
                <Button
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  href="https://gemini.google.com/app"
                  target="_blank"
                  rel="noreferrer"
                >
                  Gemini
                </Button>
                <Button
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  href="https://chatgpt.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  ChatGPT
                </Button>
              </Stack>
              {aiPrompt ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  margin="dense"
                />
              ) : null}

              <Stack direction="row" spacing={1} alignItems="center" mt={2}>
                <Box
                  sx={{
                    width: 120,
                    aspectRatio: '3 / 2',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#0F172A',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {previewImage ? (
                    <Box
                      component="img"
                      src={previewImage}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : null}
                </Box>
                <Stack spacing={1}>
                  <input
                    ref={thumbInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                  />
                  <Button
                    variant="outlined"
                    startIcon={
                      thumbUploading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CloudUploadOutlinedIcon />
                      )
                    }
                    disabled={thumbUploading}
                    onClick={() => thumbInputRef.current?.click()}
                  >
                    Upload card image
                  </Button>
                  <Button
                    size="small"
                    startIcon={<RestartAltIcon />}
                    disabled={!form.thumbnail_url || saving}
                    onClick={handleResetThumb}
                  >
                    Reset to app default
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Large AI dumps OK — auto-cropped to {MITRA_CARD_WIDTH}×{MITRA_CARD_HEIGHT} (~
                    {Math.round(MITRA_CARD_TARGET_BYTES / 1024)} KB).
                  </Typography>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <PhoneIphoneIcon fontSize="small" color="action" />
                <Typography fontWeight={700}>Live Mitra preview</Typography>
                <Chip size="small" label={langTab.toUpperCase()} />
              </Stack>
              <Box
                sx={{
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1.5,
                  bgcolor: '#F8FAFC',
                  maxWidth: 280,
                  mx: 'auto',
                }}
              >
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: 'white',
                    boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
                  }}
                >
                  <Box sx={{ position: 'relative', aspectRatio: '3 / 2', bgcolor: '#0F172A' }}>
                    {previewImage ? (
                      <Box
                        component="img"
                        src={previewImage}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Stack height="100%" alignItems="center" justifyContent="center">
                        <Typography variant="caption" color="grey.400">
                          Default asset
                        </Typography>
                      </Stack>
                    )}
                    {previewBadge ? (
                      <Chip
                        label={previewBadge}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(255,255,255,0.92)',
                          fontWeight: 800,
                          fontSize: 10,
                          height: 22,
                        }}
                      />
                    ) : null}
                  </Box>
                  <Box px={1.5} py={1.25}>
                    <Typography fontWeight={900} fontSize={14} noWrap>
                      {previewTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {previewSub}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mt={1.5} textAlign="center">
                  Matches FeatureImageCard layout on tablet / phone.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            Save card
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Container>
  );
};

export default MitraScreens;
